import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { TOOL_ICONS } from '../lib/tool-icons';
import {
  Sunrise,
  Activity,
  MessageSquare,
  Bell,
  Users,
  Zap,
  ArrowRight,
  Anchor,
  Search,
  Sparkles,
  BarChart3,
  Shield,
  Code,
  Palette,
} from 'lucide-react';

const FEATURES = [
  {
    icon: MessageSquare,
    title: 'AI-Powered Insights',
    description: 'Ask questions about your projects in plain language and get instant, data-driven answers.',
    color: 'text-blue-500 bg-blue-500/10',
  },
  {
    icon: Sunrise,
    title: 'Morning Scan',
    description: 'Start your day with an automated briefing on what changed overnight across all your projects.',
    color: 'text-amber-500 bg-amber-500/10',
  },
  {
    icon: Activity,
    title: 'Project Health',
    description: 'Track goal progress, spot risks early, and see which projects are advancing or stalling.',
    color: 'text-green-500 bg-green-500/10',
  },
  {
    icon: Zap,
    title: 'Multi-Source Integration',
    description: 'Connect GitHub, Jira, Linear, Slack, Figma, and more. All your signals in one place.',
    color: 'text-violet-500 bg-violet-500/10',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Share projects and insights across your team so everyone stays aligned.',
    color: 'text-cyan-500 bg-cyan-500/10',
  },
  {
    icon: Bell,
    title: 'Smart Alerts',
    description: 'Get notified when project health shifts so you can act before problems grow.',
    color: 'text-rose-500 bg-rose-500/10',
  },
];

const PERSONAS = [
  {
    icon: Code,
    role: 'Engineering Managers',
    question: '"Which PRs are blocked and who\'s waiting on reviews?"',
    description: 'Get instant visibility into sprint progress, code velocity, and team bottlenecks without digging through dashboards.',
  },
  {
    icon: BarChart3,
    role: 'Product Managers',
    question: '"How is the launch tracking against our milestones?"',
    description: 'Track feature delivery, surface risks to timelines, and get AI-synthesized updates from across your tools.',
  },
  {
    icon: Palette,
    role: 'UX Partners',
    question: '"What design feedback came in from the last sprint?"',
    description: 'Stay connected to implementation progress and surface design-related discussions from Slack, PRs, and tickets.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-sm">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight">Anchor</span>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
              Sign in
            </Button>
            <Button size="sm" onClick={() => navigate('/auth')}>
              Get Started
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-fade-up" style={{ animationDelay: '0ms' }}>
            <Badge variant="glass" className="mb-6">
              For Engineering & Product Leaders
            </Badge>
          </div>

          <div className="flex items-center justify-center gap-4 mb-6 animate-fade-up" style={{ animationDelay: '60ms' }}>
            <img
              src="https://cdn.magicpatterns.com/uploads/5MtA4tXkVebawPHLJj8Kzz/Drop_Anchor.svg"
              alt="Anchor illustration"
              className="w-28 h-auto hidden sm:block"
              draggable={false}
            />
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground leading-tight">
              Drop anchor on<br />what needs clarity
            </h1>
          </div>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 animate-fade-up" style={{ animationDelay: '120ms' }}>
            Anchor connects your engineering tools and uses AI to give managers, PMs, and UX partners
            instant answers about their projects.
          </p>

          <div className="flex items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: '180ms' }}>
            <Button size="lg" className="btn-ask-anchor" onClick={() => navigate('/auth')}>
              <Sparkles className="w-5 h-5 mr-2" />
              Get Started Free
            </Button>
            <Button variant="outline" size="lg" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
              See how it works
            </Button>
          </div>

          {/* Mock prompt preview */}
          <div className="mt-16 max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: '300ms' }}>
            <div className="glass-elevated rounded-[20px] p-5 text-left">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Search className="w-5 h-5 text-primary" />
                <span className="text-sm">Ask Anchor anything about your projects...</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Sprint velocity this week?', 'Blocked PRs?', 'Design feedback summary'].map((q) => (
                  <span key={q} className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
                    {q}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Personas ── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Built for the people who lead product delivery
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Whether you manage engineers, own the roadmap, or shape the experience — Anchor has your answers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PERSONAS.map((persona, i) => (
              <Card key={persona.role} className="p-6 animate-fade-up hover:shadow-glass-lg hover:scale-[1.01]" style={{ animationDelay: `${i * 80}ms` }}>
                <div className={`w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4`}>
                  <persona.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{persona.role}</h3>
                <p className="text-sm text-primary font-medium italic mb-3">{persona.question}</p>
                <p className="text-sm text-muted-foreground">{persona.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Everything you need to lead with clarity
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              From daily briefings to real-time project health, Anchor keeps you informed without the context switching.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <Card key={feature.title} className="p-6 animate-fade-up hover:shadow-glass-lg hover:scale-[1.01]" style={{ animationDelay: `${i * 60}ms` }}>
                <div className={`w-10 h-10 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-base mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integrations ── */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Connects to the tools you already use
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-12">
            Pull signals from your entire workflow into one unified view.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6">
            {TOOL_ICONS.map((tool, i) => (
              <div
                key={tool.id}
                className="glass-subtle rounded-2xl p-5 flex flex-col items-center gap-2.5 w-24 animate-fade-up hover:shadow-glass transition-all duration-200"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <tool.icon className="w-8 h-8 text-foreground/70" />
                <span className="text-xs font-medium text-muted-foreground">{tool.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="glass-elevated rounded-[24px] p-10 sm:p-14 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mx-auto mb-6 shadow-sm">
              <Anchor className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Ready to drop anchor?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
              Start getting clarity on your projects in minutes. No credit card required.
            </p>
            <Button size="lg" className="btn-ask-anchor" onClick={() => navigate('/auth')}>
              <Sparkles className="w-5 h-5 mr-2" />
              Get Started Free
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4" />
            <span>Anchor</span>
          </div>
          <span>&copy; {new Date().getFullYear()} Anchor. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
